import { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import useModal from 'lib/hooks/useModal';
import { ia, mString } from 'lib/helpers/utility';
import useScrollBlock from 'lib/hooks/useScrollBlock';
import { useUIContext } from 'lib/context/UIContext/UIContext';

import Button from 'components/shared/Button/Button';
import { showAlert } from 'components/shared/Alert/Alert';
import ProcessPayment from 'components/Patient/checkin/SecurePayment/components/ProcessPayment';
import { PaymentContextProvider } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import SuccessPayment from '../../SuccessPayment';

import KioskAutoStart from './components/KioskAutoStart';

const SecurePayment = ({
  amountDue,
  surchargeAmount = 0,
  isKioskInvoice,
  practice,
  patient,
  token,
  onSuccess,
  invoice
}) => {
  const { isOpen, openModal, closeModal } = useModal();
  const [autoStarted, setAutoStarted] = useState(false);
  const queryClient = useQueryClient();
  const { device } = useUIContext();
  const isMobile = device === 'tablet-sm' || device === 'mobile';

  const [blockScroll, allowScroll] = useScrollBlock();

  const onSuccessHandler = (data) => {
    onSuccess(data);
    queryClient.invalidateQueries(['invoice', token]);
  };

  const onError = () => {
    allowScroll();

    showAlert({
      color: 'danger',
      variant: 'lighter',
      title: 'Payment failed',
      message: 'Payment failed. Please try again.',
      icon: 'danger-mono'
    });
  };

  const onFailHandler = (error) => {
    allowScroll();
    closeModal();

    const errorMessage =
      (ia(error) ? error.join(' ') : error) || 'Payment failed. Please try again.';

    showAlert({
      color: 'danger',
      variant: 'filled',
      title: 'Payment failed',
      message: errorMessage,
      icon: 'error-filled'
    });
  };

  const startPayment = useCallback(
    (isAutoStart) => {
      setAutoStarted(isAutoStart);
      blockScroll();
      openModal();
    },
    [blockScroll, openModal]
  );

  const openModalHandler = () => startPayment(false);

  const autoStartHandler = useCallback(() => startPayment(true), [startPayment]);

  const closeModalHandler = () => {
    allowScroll();
    closeModal();
    setAutoStarted(false);
  };

  const isDisabled = invoice?.paid || amountDue <= 0;
  const requiresInvoiceReview = !!practice?.display_settings?.require_invoice_review;

  const getTooltipMessage = () => {
    if (invoice?.paid) {
      return 'This invoice has already been paid';
    }
    if (amountDue <= 0) {
      return 'No amount due';
    }
    return '';
  };

  return (
    <PaymentContextProvider
      amount={amountDue}
      surchargeAmount={surchargeAmount}
      invoiceToken={token}
      patientId={patient?.id}
      practiceId={practice?.id}
      onSuccess={onSuccessHandler}
      onError={onError}
      initialMethod="credit-card">
      <KioskAutoStart
        enabled={!isDisabled && !requiresInvoiceReview && !!isKioskInvoice}
        onStart={autoStartHandler}
      />
      <div className="absolute bottom-0 left-0 right-0 flex flex-col items-end justify-center gap-2 border-0 border-t border-solid border-t-neutral-200 bg-white px-8 py-4 sm:px-4">
        <Button
          variant="primary"
          size={isMobile ? 'large' : 'medium'}
          text={`Pay ${mString(amountDue)}`}
          onClick={openModalHandler}
          isDisabled={isDisabled}
          tooltip={isDisabled ? getTooltipMessage() : ''}
          wrapperClassName="sm:w-full"
          className="sm:w-full"
        />
      </div>
      <ProcessPayment
        isOpen={isOpen}
        handleClose={closeModalHandler}
        invoice={invoice}
        patientId={patient?.id}
        onSuccess={onSuccessHandler}
        onFail={onFailHandler}
        practice={practice}
        amount={amountDue}
        SuccessScreen={SuccessPayment}
        token={token}
        autoStarted={autoStarted}
      />
    </PaymentContextProvider>
  );
};

export default SecurePayment;
